import { useState, useEffect, useRef, useCallback } from "react";
import { devApi } from "./api";

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bg:      "#080c12", surface: "#111620", card:    "#0d1624",
  border:  "#1e2d40", accent:  "#0ea5e9", muted:   "#64748b",
  text:    "#e2e8f0", success: "#22c55e", danger:  "#ef4444",
  warning: "#f59e0b", purple:  "#a78bfa",
};

const pill = (type) => {
  const m = {
    active:   { bg: "#052e16", color: C.success, border: "#166534" },
    inactive: { bg: "#450a0a", color: C.danger,  border: "#7f1d1d" },
    paid:     { bg: "#0c1a2e", color: C.accent,  border: "#1e3a5f" },
    unpaid:   { bg: "#1a1a2e", color: C.muted,   border: C.border  },
    warning:  { bg: "#431407", color: C.warning, border: "#7c2d12" },
  };
  const t = m[type] || m.unpaid;
  return { display:"inline-block", padding:"2px 8px", borderRadius:4,
           fontSize:11, fontWeight:600, background:t.bg, color:t.color,
           border:`1px solid ${t.border}` };
};

// ── Shared primitives ─────────────────────────────────────────────────────────
const Input = ({ label, ...p }) => (
  <div style={{ marginBottom: 14 }}>
    {label && <div style={{ fontSize:11, color:C.muted, marginBottom:5, letterSpacing:.5 }}>{label}</div>}
    <input style={{
      width:"100%", background:C.card, border:`1px solid ${C.border}`,
      borderRadius:6, padding:"9px 12px", color:C.text, fontSize:13,
      outline:"none", boxSizing:"border-box", fontFamily:"inherit",
    }} {...p} />
  </div>
);

const Checkbox = ({ label, checked, onChange }) => (
  <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer",
                  fontSize:13, color:C.text, marginBottom:10 }}>
    <input type="checkbox" checked={checked} onChange={onChange}
           style={{ width:14, height:14, cursor:"pointer" }} />
    {label}
  </label>
);

const Btn = ({ children, variant="primary", loading, ...p }) => {
  const bg = variant==="danger" ? C.danger : variant==="ghost"
    ? "#1e2d40" : variant==="success" ? "#166534" : C.accent;
  return (
    <button style={{
      background:bg, color:"#fff", border:"none", borderRadius:6,
      padding:"9px 18px", fontSize:13, fontWeight:600, cursor:"pointer",
      fontFamily:"inherit", opacity: p.disabled ? .5 : 1,
    }} {...p}>
      {loading ? "Please wait…" : children}
    </button>
  );
};

const ErrorBox = ({ msg }) => msg ? (
  <div style={{ background:"#450a0a", border:`1px solid #7f1d1d`, borderRadius:6,
                padding:"9px 12px", color:C.danger, fontSize:12, marginBottom:12 }}>
    {msg}
  </div>
) : null;

const SuccessBox = ({ msg }) => msg ? (
  <div style={{ background:"#052e16", border:`1px solid #166534`, borderRadius:6,
                padding:"9px 12px", color:C.success, fontSize:12, marginBottom:12 }}>
    {msg}
  </div>
) : null;

const SectionTitle = ({ children }) => (
  <div style={{ fontSize:11, fontWeight:700, letterSpacing:2, textTransform:"uppercase",
                color:C.muted, marginBottom:20 }}>
    {children}
  </div>
);

const Divider = () => <div style={{ borderTop:`1px solid ${C.border}`, margin:"20px 0" }} />;

const Table = ({ cols, rows, emptyMsg = "No records found." }) => (
  rows.length === 0
    ? <div style={{ color:C.muted, fontSize:13, padding:"16px 0" }}>{emptyMsg}</div>
    : <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
        <thead>
          <tr>{cols.map(c => (
            <th key={c} style={{ textAlign:"left", padding:"6px 10px", color:C.muted,
                                 fontWeight:600, borderBottom:`1px solid ${C.border}`,
                                 fontSize:11, whiteSpace:"nowrap" }}>
              {c}
            </th>
          ))}</tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
);

const TD = ({ children, muted }) => (
  <td style={{ padding:"8px 10px", borderBottom:`1px solid ${C.border}`,
               color: muted ? C.muted : C.text }}>
    {children}
  </td>
);


// ══════════════════════════════════════════════════════════════════════════════
// SECTION: TENANTS
// ══════════════════════════════════════════════════════════════════════════════
function TenantsSection({ tenants, loading, onRefresh }) {
  const [form,          setForm]         = useState({ tenant_id:"", name:"", billing_email:"", primary_color:"#00dc96" });
  const [saving,        setSaving]       = useState(false);
  const [toggling,      setToggling]     = useState(null);
  const [impersonating, setImpersonating]= useState(null);
  const [err,           setErr]          = useState("");
  const [ok,            setOk]           = useState("");

  const handleCreate = async (e) => {
    e.preventDefault();
    setErr(""); setOk("");
    if (!form.tenant_id || !form.name) { setErr("Tenant ID and company name are required."); return; }
    setSaving(true);
    try {
      const res = await devApi.createTenant(form);
      setOk(res.message);
      setForm({ tenant_id:"", name:"", billing_email:"", primary_color:"#00dc96" });
      onRefresh();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const handleToggle = async (tid, isActive) => {
    if (isActive) {
      const confirmed = window.confirm(
        `Deactivate "${tid}"?\n\nAll users of this tenant will be locked out immediately.`
      );
      if (!confirmed) return;
    }
    setToggling(tid);
    try {
      const res = await devApi.toggleTenantStatus(tid);
      setOk(res.message);
      onRefresh();
    } catch (e) { setErr(e.message); }
    finally { setToggling(null); }
  };

  const handleImpersonate = async (tid) => {
    setImpersonating(tid); setErr(""); setOk("");
    try {
      await devApi.impersonate(tid);
      window.location.href = "/";
    } catch (e) { setErr(e.message); setImpersonating(null); }
  };

  return (
    <div>
      <SectionTitle>All Tenants</SectionTitle>
      <ErrorBox msg={err} /><SuccessBox msg={ok} />

      {loading ? <div style={{ color:C.muted, fontSize:13 }}>Loading…</div> : (
        <Table
          cols={["Tenant ID", "Company", "Employees", "Status", "Status Action", "Dev Login"]}
          emptyMsg="No tenants yet. Create the first one below."
          rows={tenants.map(t => (
            <tr key={t.tenant_id}>
              <TD><span style={{ color:C.accent, fontWeight:600 }}>{t.tenant_id}</span></TD>
              <TD>{t.name}</TD>
              <TD muted>{t.emp_count}</TD>
              <TD><span style={pill(t.is_active ? "active" : "inactive")}>
                {t.is_active ? "Active" : "Inactive"}
              </span></TD>
              <TD>
                <Btn
                  variant={t.is_active ? "danger" : "success"}
                  disabled={toggling === t.tenant_id}
                  loading={toggling === t.tenant_id}
                  onClick={() => handleToggle(t.tenant_id, t.is_active)}
                  style={{ padding:"5px 12px", fontSize:12 }}
                >
                  {t.is_active ? "Deactivate" : "Activate"}
                </Btn>
              </TD>
              <TD>
                <Btn
                  variant="ghost"
                  disabled={!t.is_active || impersonating === t.tenant_id}
                  loading={impersonating === t.tenant_id}
                  onClick={() => handleImpersonate(t.tenant_id)}
                  style={{ padding:"5px 12px", fontSize:12, whiteSpace:"nowrap",
                           background:"#1a0a3a", color: t.is_active ? C.purple : C.muted,
                           border:`1px solid ${t.is_active ? "#5b21b6" : C.border}` }}
                  title={!t.is_active ? "Tenant is inactive" : "Open HR Admin view in this tab"}
                >
                  Login as Admin
                </Btn>
              </TD>
            </tr>
          ))}
        />
      )}

      <Divider />
      <SectionTitle>Create New Tenant</SectionTitle>

      <form onSubmit={handleCreate}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <Input
            label="TENANT ID (slug)"
            placeholder="e.g. acme-corp"
            value={form.tenant_id}
            onChange={e => setForm(f => ({ ...f, tenant_id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,"") }))}
          />
          <Input
            label="COMPANY NAME"
            placeholder="e.g. Acme Corporation"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          />
          <Input
            label="BILLING EMAIL (optional)"
            placeholder="billing@company.com"
            type="email"
            value={form.billing_email}
            onChange={e => setForm(f => ({ ...f, billing_email: e.target.value }))}
          />
          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:11, color:C.muted, marginBottom:5, letterSpacing:.5 }}>BRAND COLOR</div>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <input type="color" value={form.primary_color}
                     onChange={e => setForm(f => ({ ...f, primary_color: e.target.value }))}
                     style={{ width:40, height:36, border:"none", cursor:"pointer",
                              borderRadius:4, background:"transparent" }} />
              <span style={{ color:C.muted, fontSize:12 }}>{form.primary_color}</span>
            </div>
          </div>
        </div>

        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:6,
                      padding:"10px 14px", fontSize:11, color:C.muted, marginBottom:16 }}>
          Plan assigned: <span style={{ color:C.accent }}>Pro</span> (all features, unlimited employees & offices)
        </div>

        <Btn type="submit" loading={saving} disabled={saving}>Create Tenant</Btn>
      </form>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// SECTION: LEAVE TYPES
// ══════════════════════════════════════════════════════════════════════════════
function LeaveTypesSection({ tenants }) {
  const [selectedTenant, setSelected]   = useState("");
  const [leaveTypes,     setLeaveTypes] = useState([]);
  const [ltLoading,      setLtLoading]  = useState(false);
  const [form,           setForm]       = useState({ name:"", max_days_yearly:"", is_paid:true, carry_forward:false });
  const [saving,         setSaving]     = useState(false);
  const [deleting,       setDeleting]   = useState(null);
  const [err,            setErr]        = useState("");
  const [ok,             setOk]         = useState("");

  const loadTypes = useCallback(async (tid) => {
    if (!tid) { setLeaveTypes([]); return; }
    setLtLoading(true); setErr(""); setOk("");
    try {
      setLeaveTypes(await devApi.getLeaveTypes(tid) || []);
    } catch (e) { setErr(e.message); }
    finally { setLtLoading(false); }
  }, []);

  useEffect(() => { loadTypes(selectedTenant); }, [selectedTenant, loadTypes]);

  useEffect(() => {
    if (tenants.length > 0 && !selectedTenant) setSelected(tenants[0].tenant_id);
  }, [tenants, selectedTenant]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setErr(""); setOk("");
    if (!form.name.trim() || !form.max_days_yearly) { setErr("Name and max days are required."); return; }
    const days = parseInt(form.max_days_yearly, 10);
    if (isNaN(days) || days < 1) { setErr("Max days must be a positive number."); return; }
    setSaving(true);
    try {
      const res = await devApi.addLeaveType(selectedTenant, { ...form, max_days_yearly: days });
      setOk(res.message);
      setForm({ name:"", max_days_yearly:"", is_paid:true, carry_forward:false });
      loadTypes(selectedTenant);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (lt) => {
    const confirmed = window.confirm(`Delete leave type "${lt.name}"?\n\nThis cannot be undone.`);
    if (!confirmed) return;
    setDeleting(lt.id); setErr(""); setOk("");
    try {
      const res = await devApi.deleteLeaveType(selectedTenant, lt.id);
      setOk(res.message);
      loadTypes(selectedTenant);
    } catch (e) { setErr(e.message); }
    finally { setDeleting(null); }
  };

  return (
    <div>
      <SectionTitle>Leave Types</SectionTitle>

      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:11, color:C.muted, marginBottom:5, letterSpacing:.5 }}>TENANT</div>
        <select
          value={selectedTenant}
          onChange={e => setSelected(e.target.value)}
          style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:6,
                   padding:"9px 12px", color:C.text, fontSize:13, outline:"none",
                   fontFamily:"inherit", minWidth:260 }}
        >
          {tenants.map(t => (
            <option key={t.tenant_id} value={t.tenant_id}>
              {t.name} ({t.tenant_id})
            </option>
          ))}
        </select>
      </div>

      <ErrorBox msg={err} /><SuccessBox msg={ok} />

      {ltLoading ? (
        <div style={{ color:C.muted, fontSize:13, marginBottom:20 }}>Loading…</div>
      ) : (
        <Table
          cols={["#", "Leave Type", "Days/Year", "Paid", "Carry Forward", "Action"]}
          emptyMsg="No leave types yet for this tenant."
          rows={leaveTypes.map(lt => (
            <tr key={lt.id}>
              <TD muted>{lt.id}</TD>
              <TD><strong>{lt.name}</strong></TD>
              <TD muted>{lt.max_days_yearly}</TD>
              <TD><span style={pill(lt.is_paid ? "paid" : "unpaid")}>{lt.is_paid ? "Paid" : "Unpaid"}</span></TD>
              <TD muted>{lt.carry_forward ? "Yes" : "No"}</TD>
              <TD>
                <Btn
                  variant="danger"
                  disabled={deleting === lt.id}
                  loading={deleting === lt.id}
                  onClick={() => handleDelete(lt)}
                  style={{ padding:"5px 12px", fontSize:12 }}
                >
                  Delete
                </Btn>
              </TD>
            </tr>
          ))}
        />
      )}

      <Divider />
      <SectionTitle>Add Leave Type</SectionTitle>

      <form onSubmit={handleAdd}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <Input
            label="LEAVE TYPE NAME"
            placeholder="e.g. Annual Leave"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          />
          <Input
            label="MAX DAYS PER YEAR"
            type="number" min="1" max="365"
            placeholder="e.g. 21"
            value={form.max_days_yearly}
            onChange={e => setForm(f => ({ ...f, max_days_yearly: e.target.value }))}
          />
        </div>
        <Checkbox label="Paid leave"       checked={form.is_paid}       onChange={e => setForm(f => ({ ...f, is_paid:       e.target.checked }))} />
        <Checkbox label="Allow carry forward to next year" checked={form.carry_forward} onChange={e => setForm(f => ({ ...f, carry_forward: e.target.checked }))} />
        <div style={{ marginTop:4 }}>
          <Btn type="submit" loading={saving} disabled={saving || !selectedTenant}>Add Leave Type</Btn>
        </div>
      </form>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// SECTION: BULK IMPORT
// ══════════════════════════════════════════════════════════════════════════════
function BulkImportSection({ tenants }) {
  const [selectedTenant, setSelected]  = useState("");
  const [file,           setFile]      = useState(null);
  const [dragOver,       setDragOver]  = useState(false);
  const [importing,      setImporting] = useState(false);
  const [err,            setErr]       = useState("");
  const [result,         setResult]    = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (tenants.length > 0 && !selectedTenant) setSelected(tenants[0].tenant_id);
  }, [tenants, selectedTenant]);

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.endsWith(".xlsx")) { setErr("Only .xlsx files are accepted."); return; }
    if (f.size > 5 * 1024 * 1024)  { setErr("File too large. Maximum 5 MB."); return; }
    setErr(""); setResult(null); setFile(f);
  };

  const handleImport = async () => {
    if (!selectedTenant) { setErr("Select a tenant first."); return; }
    if (!file)           { setErr("Select a file first."); return; }
    setImporting(true); setErr(""); setResult(null);
    try {
      const res = await devApi.importEmployees(selectedTenant, file);
      if (!res) throw new Error("Session expired.");
      setResult(res); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) { setErr(e.message); }
    finally { setImporting(false); }
  };

  return (
    <div>
      <SectionTitle>Bulk Employee Import</SectionTitle>

      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:6,
                    padding:"12px 14px", fontSize:11, color:C.muted, lineHeight:1.7, marginBottom:20 }}>
        <strong style={{ color:C.text }}>Required columns:</strong>{" "}
        name &nbsp;·&nbsp; email &nbsp;·&nbsp; password
        <br />
        <strong style={{ color:C.text }}>Optional columns:</strong>{" "}
        role &nbsp;·&nbsp; department &nbsp;·&nbsp; office_id &nbsp;·&nbsp;
        shift_start &nbsp;·&nbsp; shift_end &nbsp;·&nbsp; late_grace_mins &nbsp;·&nbsp;
        joining_date &nbsp;·&nbsp; address_line1 &nbsp;·&nbsp; city &nbsp;·&nbsp;
        state &nbsp;·&nbsp; postal_code &nbsp;·&nbsp; country
        <br />
        <span style={{ color:C.warning }}>
          Date format: YYYY-MM-DD &nbsp;·&nbsp; Time format: HH:MM &nbsp;·&nbsp; Max file: 5 MB
        </span>
      </div>

      <div style={{ marginBottom:16 }}>
        <div style={{ fontSize:11, color:C.muted, marginBottom:5, letterSpacing:.5 }}>TARGET TENANT</div>
        <select
          value={selectedTenant}
          onChange={e => { setSelected(e.target.value); setResult(null); }}
          style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:6,
                   padding:"9px 12px", color:C.text, fontSize:13, outline:"none",
                   fontFamily:"inherit", minWidth:260 }}
        >
          {tenants.map(t => (
            <option key={t.tenant_id} value={t.tenant_id}>
              {t.name} ({t.tenant_id}){!t.is_active ? " — INACTIVE" : ""}
            </option>
          ))}
        </select>
      </div>

      <ErrorBox msg={err} />

      <div
        style={{
          border:`2px dashed ${dragOver || file ? C.accent : C.border}`,
          borderRadius:8, padding:"20px 16px", textAlign:"center",
          color: dragOver || file ? C.accent : C.muted,
          cursor:"pointer", marginBottom:14, transition:"all .15s",
          background: dragOver ? "#0c1d2e" : "transparent",
        }}
        onClick={() => fileRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
      >
        {file
          ? <span>📄 {file.name} <span style={{ color:C.muted }}>({(file.size/1024).toFixed(1)} KB)</span></span>
          : <span>Drop .xlsx here &nbsp;or&nbsp; <span style={{ color:C.accent }}>click to browse</span></span>
        }
      </div>
      <input ref={fileRef} type="file" accept=".xlsx" style={{ display:"none" }}
             onChange={e => handleFile(e.target.files[0])} />

      <div style={{ display:"flex", gap:10 }}>
        <Btn onClick={handleImport} loading={importing} disabled={importing || !file || !selectedTenant}>
          Import Employees
        </Btn>
        {file && (
          <Btn variant="ghost" onClick={() => { setFile(null); setResult(null); if (fileRef.current) fileRef.current.value=""; }}>
            Clear
          </Btn>
        )}
      </div>

      {result && (
        <>
          <Divider />
          <div style={{ display:"flex", gap:16, marginBottom: result.errors.length ? 16 : 0 }}>
            <span style={pill("active")}>{result.inserted} inserted</span>
            <span style={pill(result.skipped > 0 ? "warning" : "unpaid")}>{result.skipped} skipped</span>
          </div>
          {result.errors.length > 0 && (
            <Table
              cols={["Row", "Email", "Reason"]}
              rows={result.errors.map((e, i) => (
                <tr key={i}>
                  <TD muted>{e.row}</TD>
                  <TD muted>{e.email || "—"}</TD>
                  <TD><span style={{ color:C.danger }}>{e.reason}</span></TD>
                </tr>
              ))}
            />
          )}
        </>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// SECTION: OFFICES
// ══════════════════════════════════════════════════════════════════════════════
const EMPTY_OFFICE = { name:"", city:"", country:"", address:"", latitude:"", longitude:"", radius_meters:100 };

function OfficesSection({ tenants }) {
  const [selectedTenant, setSelected]  = useState("");
  const [offices,        setOffices]   = useState([]);
  const [loading,        setLoading]   = useState(false);
  const [editingId,      setEditingId] = useState(null);
  const [editForm,       setEditForm]  = useState(EMPTY_OFFICE);
  const [addForm,        setAddForm]   = useState(EMPTY_OFFICE);
  const [saving,         setSaving]    = useState(false);
  const [deleting,       setDeleting]  = useState(null);
  const [err,            setErr]       = useState("");
  const [ok,             setOk]        = useState("");

  const loadOffices = useCallback(async (tid) => {
    if (!tid) { setOffices([]); return; }
    setLoading(true); setErr(""); setOk("");
    try { setOffices(await devApi.getOffices(tid) || []); }
    catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadOffices(selectedTenant); }, [selectedTenant, loadOffices]);

  useEffect(() => {
    if (tenants.length > 0 && !selectedTenant) setSelected(tenants[0].tenant_id);
  }, [tenants, selectedTenant]);

  const validateCoords = (lat, lng) => {
    const la = parseFloat(lat), lo = parseFloat(lng);
    if (isNaN(la) || la < -90  || la > 90)  return "Latitude must be between -90 and 90.";
    if (isNaN(lo) || lo < -180 || lo > 180) return "Longitude must be between -180 and 180.";
    return null;
  };

  const handleAdd = async (e) => {
    e.preventDefault(); setErr(""); setOk("");
    if (!addForm.name.trim()) { setErr("Office name is required."); return; }
    const coordErr = validateCoords(addForm.latitude, addForm.longitude);
    if (coordErr) { setErr(coordErr); return; }
    setSaving(true);
    try {
      const res = await devApi.createOffice(selectedTenant, {
        name:          addForm.name.trim(),
        city:          addForm.city     || null,
        country:       addForm.country  || null,
        address:       addForm.address  || null,
        latitude:      parseFloat(addForm.latitude),
        longitude:     parseFloat(addForm.longitude),
        radius_meters: parseInt(addForm.radius_meters, 10) || 100,
      });
      setOk(res.message || "Office created.");
      setAddForm(EMPTY_OFFICE);
      loadOffices(selectedTenant);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const startEdit = (o) => {
    setEditingId(o.id);
    setEditForm({
      name:          o.name,
      city:          o.city          || "",
      country:       o.country       || "",
      address:       o.address       || "",
      latitude:      o.latitude      != null ? o.latitude  : "",
      longitude:     o.longitude     != null ? o.longitude : "",
      radius_meters: o.radius_meters ?? 100,
    });
  };

  const handleSaveEdit = async () => {
    setErr(""); setOk("");
    const coordErr = validateCoords(editForm.latitude, editForm.longitude);
    if (coordErr) { setErr(coordErr); return; }
    setSaving(true);
    try {
      const res = await devApi.updateOffice(selectedTenant, editingId, {
        name:          editForm.name.trim() || undefined,
        city:          editForm.city     || null,
        country:       editForm.country  || null,
        address:       editForm.address  || null,
        latitude:      parseFloat(editForm.latitude),
        longitude:     parseFloat(editForm.longitude),
        radius_meters: parseInt(editForm.radius_meters, 10) || 100,
      });
      setOk(res.message || "Office updated.");
      setEditingId(null);
      loadOffices(selectedTenant);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (o) => {
    if (!window.confirm(`Delete office "${o.name}"?\n\n${o.emp_count > 0 ? `⚠ This office has ${o.emp_count} active employee(s).` : "This cannot be undone."}`)) return;
    setDeleting(o.id); setErr(""); setOk("");
    try {
      const res = await devApi.deleteOffice(selectedTenant, o.id);
      setOk(res.message || "Office deleted.");
      loadOffices(selectedTenant);
    } catch (e) { setErr(e.message); }
    finally { setDeleting(null); }
  };

  const OfficeFormFields = ({ form, onChange }) => (
    <>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
        <Input label="OFFICE NAME *"  placeholder="e.g. HQ Mumbai"     value={form.name}      onChange={e => onChange("name",          e.target.value)} />
        <Input label="CITY"           placeholder="Mumbai"              value={form.city}      onChange={e => onChange("city",          e.target.value)} />
        <Input label="COUNTRY"        placeholder="India"               value={form.country}   onChange={e => onChange("country",       e.target.value)} />
        <Input label="ADDRESS"        placeholder="Street, Area"        value={form.address}   onChange={e => onChange("address",       e.target.value)} />
        <Input label="LATITUDE *"     placeholder="18.9220"  type="number" step="any"
               value={form.latitude}  onChange={e => onChange("latitude",  e.target.value)} />
        <Input label="LONGITUDE *"    placeholder="72.8347"  type="number" step="any"
               value={form.longitude} onChange={e => onChange("longitude", e.target.value)} />
        <Input label="RADIUS (metres)" placeholder="100"     type="number" min="10" max="5000"
               value={form.radius_meters} onChange={e => onChange("radius_meters", e.target.value)} />
      </div>
    </>
  );

  return (
    <div>
      <SectionTitle>Offices</SectionTitle>

      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:11, color:C.muted, marginBottom:5, letterSpacing:.5 }}>TENANT</div>
        <select value={selectedTenant} onChange={e => { setSelected(e.target.value); setEditingId(null); }}
          style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:6,
                   padding:"9px 12px", color:C.text, fontSize:13, outline:"none",
                   fontFamily:"inherit", minWidth:260 }}>
          {tenants.map(t => (
            <option key={t.tenant_id} value={t.tenant_id}>{t.name} ({t.tenant_id})</option>
          ))}
        </select>
      </div>

      <ErrorBox msg={err} /><SuccessBox msg={ok} />

      {loading ? (
        <div style={{ color:C.muted, fontSize:13, marginBottom:20 }}>Loading…</div>
      ) : offices.length === 0 ? (
        <div style={{ color:C.muted, fontSize:13, marginBottom:20 }}>No offices yet for this tenant.</div>
      ) : (
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12, marginBottom:20 }}>
          <thead>
            <tr>{["#","Name","City","Country","Lat","Lng","Radius","Staff","Actions"].map(c => (
              <th key={c} style={{ textAlign:"left", padding:"6px 10px", color:C.muted,
                                   fontWeight:600, borderBottom:`1px solid ${C.border}`,
                                   fontSize:11, whiteSpace:"nowrap" }}>{c}</th>
            ))}</tr>
          </thead>
          <tbody>
            {offices.map(o => editingId === o.id ? (
              <tr key={o.id} style={{ background:"#0c1d2e" }}>
                <td colSpan={9} style={{ padding:"14px 10px", borderBottom:`1px solid ${C.border}` }}>
                  <div style={{ marginBottom:10, fontSize:11, color:C.accent, fontWeight:700 }}>
                    Editing: {o.name}
                  </div>
                  <OfficeFormFields
                    form={editForm}
                    onChange={(k, v) => setEditForm(f => ({ ...f, [k]: v }))}
                  />
                  <div style={{ display:"flex", gap:10, marginTop:4 }}>
                    <Btn onClick={handleSaveEdit} loading={saving} disabled={saving}>Save Changes</Btn>
                    <Btn variant="ghost" onClick={() => setEditingId(null)} disabled={saving}>Cancel</Btn>
                  </div>
                </td>
              </tr>
            ) : (
              <tr key={o.id}>
                <TD muted>{o.id}</TD>
                <TD><strong>{o.name}</strong></TD>
                <TD muted>{o.city  || "—"}</TD>
                <TD muted>{o.country || "—"}</TD>
                <TD muted>{o.latitude  != null ? o.latitude.toFixed(4)  : "—"}</TD>
                <TD muted>{o.longitude != null ? o.longitude.toFixed(4) : "—"}</TD>
                <TD muted>{o.radius_meters}m</TD>
                <TD muted>{o.emp_count}</TD>
                <TD>
                  <div style={{ display:"flex", gap:6 }}>
                    <Btn variant="ghost" style={{ padding:"4px 10px", fontSize:11 }} onClick={() => startEdit(o)}>
                      Edit
                    </Btn>
                    <Btn variant="danger" style={{ padding:"4px 10px", fontSize:11 }}
                         disabled={deleting === o.id} loading={deleting === o.id}
                         onClick={() => handleDelete(o)}>
                      Delete
                    </Btn>
                  </div>
                </TD>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Divider />
      <SectionTitle>Add Office</SectionTitle>
      <form onSubmit={handleAdd}>
        <OfficeFormFields form={addForm} onChange={(k, v) => setAddForm(f => ({ ...f, [k]: v }))} />
        <Btn type="submit" loading={saving} disabled={saving || !selectedTenant}>Add Office</Btn>
      </form>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// SECTION: AUDIT LOG
// ══════════════════════════════════════════════════════════════════════════════
function AuditLogSection() {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    devApi.getAuditLog(100)
      .then(r => setRows(r || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const actionColor = (a) => {
    if (a === "LOGIN_SUCCESS" || a.endsWith("_CREATE") || a.endsWith("_ADD") || a === "BULK_IMPORT") return C.success;
    if (a === "LOGIN_FAIL")                                                                           return C.danger;
    if (a.endsWith("_DEACTIVATE") || a.endsWith("_DELETE"))                                          return C.warning;
    return C.muted;
  };

  return (
    <div>
      <SectionTitle>Audit Log</SectionTitle>
      {loading
        ? <div style={{ color:C.muted, fontSize:13 }}>Loading…</div>
        : <Table
            cols={["Action", "Detail", "IP", "Time"]}
            emptyMsg="No activity yet."
            rows={rows.map(r => (
              <tr key={r.id}>
                <TD>
                  <span style={{ color:actionColor(r.action), fontWeight:600, fontSize:11 }}>
                    {r.action}
                  </span>
                </TD>
                <TD muted>
                  <span style={{ maxWidth:280, overflow:"hidden", textOverflow:"ellipsis",
                                 whiteSpace:"nowrap", display:"block" }}>
                    {r.detail || "—"}
                  </span>
                </TD>
                <TD muted>{r.ip_address || "—"}</TD>
                <TD muted>{r.actioned_at ? new Date(r.actioned_at).toLocaleString() : "—"}</TD>
              </tr>
            ))}
          />
      }
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// MAIN PANEL (sidebar + content)
// ══════════════════════════════════════════════════════════════════════════════
const NAV = [
  { id:"tenants",     label:"Tenants",      icon:"🏢" },
  { id:"offices",     label:"Offices",      icon:"🏬" },
  { id:"leavetypes",  label:"Leave Types",  icon:"📋" },
  { id:"bulkimport",  label:"Bulk Import",  icon:"📥" },
  { id:"auditlog",    label:"Audit Log",    icon:"🔍" },
];

function Panel({ onLogout }) {
  const [section,  setSection]  = useState("tenants");
  const [tenants,  setTenants]  = useState([]);
  const [tLoading, setTLoading] = useState(true);

  const loadTenants = useCallback(() => {
    setTLoading(true);
    devApi.getTenants()
      .then(t => setTenants(t || []))
      .catch(() => {})
      .finally(() => setTLoading(false));
  }, []);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  const content = {
    tenants:    <TenantsSection    tenants={tenants} loading={tLoading} onRefresh={loadTenants} />,
    offices:    <OfficesSection    tenants={tenants} />,
    leavetypes: <LeaveTypesSection tenants={tenants} />,
    bulkimport: <BulkImportSection tenants={tenants} />,
    auditlog:   <AuditLogSection />,
  }[section];

  return (
    <div style={{ minHeight:"100vh", background:C.bg, color:C.text,
                  fontFamily:"'DM Mono','Fira Mono',monospace", fontSize:14,
                  display:"flex", flexDirection:"column" }}>

      {/* Header */}
      <div style={{ background:C.surface, borderBottom:`1px solid ${C.border}`,
                    padding:"13px 24px", display:"flex", alignItems:"center",
                    justifyContent:"space-between" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <span style={{ color:C.accent, fontWeight:700, fontSize:15, letterSpacing:1 }}>⬡ ATTENDTRACK</span>
          <span style={{ background:"#1a2535", border:`1px solid ${C.border}`, color:C.muted,
                         fontSize:11, padding:"2px 8px", borderRadius:4 }}>
            DEVELOPER PANEL
          </span>
        </div>
        <button onClick={() => { devApi.logout(); onLogout(); }}
                style={{ background:"#1e2d40", color:C.text, border:"none", borderRadius:6,
                         padding:"7px 14px", fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>
          Log out
        </button>
      </div>

      {/* Body */}
      <div style={{ display:"flex", flex:1 }}>

        {/* Sidebar */}
        <div style={{ width:200, background:C.surface, borderRight:`1px solid ${C.border}`,
                      padding:"20px 0", flexShrink:0 }}>
          {NAV.map(n => (
            <div
              key={n.id}
              onClick={() => setSection(n.id)}
              style={{
                padding:"11px 20px", cursor:"pointer", display:"flex", alignItems:"center", gap:10,
                fontSize:13, fontWeight: section===n.id ? 600 : 400,
                color:      section===n.id ? C.accent : C.muted,
                background: section===n.id ? "#0c1d2e" : "transparent",
                borderLeft: section===n.id ? `3px solid ${C.accent}` : "3px solid transparent",
                transition: "all .12s",
              }}
            >
              <span style={{ fontSize:15 }}>{n.icon}</span>
              {n.label}
            </div>
          ))}

          {/* Tenant count badge */}
          <div style={{ margin:"24px 14px 0", background:C.card, border:`1px solid ${C.border}`,
                        borderRadius:8, padding:"12px", fontSize:11 }}>
            <div style={{ color:C.muted, marginBottom:6 }}>TOTAL TENANTS</div>
            <div style={{ fontSize:22, fontWeight:700, color:C.text }}>{tenants.length}</div>
            <div style={{ color:C.success, fontSize:11, marginTop:2 }}>
              {tenants.filter(t => t.is_active).length} active
            </div>
          </div>
        </div>

        {/* Main content */}
        <div style={{ flex:1, padding:"32px 36px", overflowY:"auto", maxWidth:900 }}>
          {content}
        </div>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// LOGIN
// ══════════════════════════════════════════════════════════════════════════════
function LoginCard({ onSuccess }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err,      setErr]      = useState("");
  const [loading,  setLoading]  = useState(false);
  const passRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!username.trim() || !password) { setErr("Both fields are required."); return; }
    setLoading(true);
    try {
      await devApi.login(username.trim(), password);
      onSuccess();
    } catch (err) {
      setErr(
        err.status === 429 ? "Too many failed attempts. Locked for 15 minutes." :
        err.status === 503 ? "Developer access is not configured on this server." :
        err.message || "Invalid credentials."
      );
      setPassword(""); passRef.current?.focus();
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", alignItems:"center",
                  justifyContent:"center", fontFamily:"'DM Mono',monospace" }}>
      <div style={{ width:360 }}>
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <div style={{ color:C.accent, fontSize:22, fontWeight:700, letterSpacing:1 }}>⬡ AttendTrack</div>
          <div style={{ color:C.muted, fontSize:12, marginTop:6, letterSpacing:1.5 }}>DEVELOPER ACCESS</div>
        </div>
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:28 }}>
          <ErrorBox msg={err} />
          <form onSubmit={submit} autoComplete="off">
            <Input label="USERNAME" type="text" value={username} autoFocus autoComplete="off"
                   onChange={e => setUsername(e.target.value)} />
            <Input label="PASSWORD" type="password" value={password} autoComplete="current-password"
                   ref_={passRef} onChange={e => setPassword(e.target.value)} />
            <button type="submit" disabled={loading} style={{
              width:"100%", padding:12, background:C.accent, color:"#fff",
              border:"none", borderRadius:6, fontSize:13, fontWeight:600,
              cursor:"pointer", fontFamily:"inherit", marginTop:6,
            }}>
              {loading ? "Verifying…" : "Sign In"}
            </button>
          </form>
        </div>
        <div style={{ textAlign:"center", color:C.muted, fontSize:11, marginTop:16 }}>
          This panel is not linked from any public page.
        </div>
      </div>
    </div>
  );
}


// ── Root export ───────────────────────────────────────────────────────────────
export default function DevPanel() {
  const [loggedIn, setLoggedIn] = useState(devApi.isLoggedIn());
  if (!loggedIn) return <LoginCard onSuccess={() => setLoggedIn(true)} />;
  return <Panel onLogout={() => setLoggedIn(false)} />;
}
